import classNames from "classnames";
import { FreeTrialRole, InitUserRole, postAsyncRequest, useUserState, UUID } from "@gx1/gx-npm-lib";
import { EyeIcon } from "@gx1/gx-npm-ui";
import { TooltipV2, TypographyComponent } from "@gx1/gx-npm-ui";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { navigateToUrl } from "single-spa";
import { HomeAppContext } from "../../../app.context";
import { Initiative } from "../../../app.types";
import styles from "./eval-card.styles.module.scss";
import EvalCardFooter from "./eval-card-footer/eval-card-footer.component";
import DeleteDialog from "./delete-dialog/delete-dialog.component";
import KebabMenuComponent from "./kebab-menu/kebab-menu.component";

type EvalItemComponentProps = { initData: Initiative; onInitiativeRemove?: (id: UUID) => void };
const EvalItemComponent = ({ initData, onInitiativeRemove = () => {} }: EvalItemComponentProps) => {
  const { t } = useTranslation();
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { setShowSnackBar, setSnackbarMessage, setSaveDataErrorStatus } = useContext(HomeAppContext);
  const { templateName, id, name, lastActivity, userRole } = initData;
  const initName = name || t("Untitled evaluation");
  const isOwner = userRole === InitUserRole.OWNER;
  const isViewOnly = userRole === InitUserRole.VIEWER;

  const { freeTrialRole } = useUserState();
  const isTrialUser = Boolean(freeTrialRole && Object.values(FreeTrialRole).includes(freeTrialRole));

  const navigateToOverview = () => {
    navigateToUrl(`/s/evaluation/${id}/overview`);
  };

  const handleInitiativeArchive = async () => {
    setIsLoading(true);
    setSnackbarMessage(`${initName} was sent to archive.`);
    const response = await postAsyncRequest(`/api/v2/initiatives/${id}/action/archive`);
    if (response.status === 201) {
      setShowSnackBar(true);
      onInitiativeRemove(id);
    } else {
      setSaveDataErrorStatus(true);
    }
    setIsLoading(false);
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    setSnackbarMessage(`${initName} was sent to trash.`);
    const response = await postAsyncRequest(`/api/v2/initiatives/${id}/action/delete`);
    if (response.status === 201) {
      setShowSnackBar(true);
      setDeleteDialog(false);
      onInitiativeRemove(id);
    } else {
      setSaveDataErrorStatus(true);
    }
    setIsLoading(false);
  };

  let kebabMenuTooltipText = t("Evaluation options");
  if (isTrialUser) {
    kebabMenuTooltipText = t("Archive and deleting is not available in the Gartner BuySmart free trial.");
  } else if (!isOwner) {
    kebabMenuTooltipText = t("This action is only available to the evaluation owner.");
  }
  return (
    <div className={styles.evalCardWrapper}>
      <button className={styles.overviewLink} aria-label={t("click for overview")} onClick={navigateToOverview}>
        <div className={styles.evalCard}>
          <div className={styles.templateWrapper}>
            <TypographyComponent boldness="semi" color="iron" styling="p4" rootClassName={styles.templateName}>
              {templateName}
            </TypographyComponent>
            {isViewOnly && (
              <div className={classNames(styles.viewOnly, !templateName && styles.noTemplateName)}>
                <EyeIcon width="20" height="18" />
                <TypographyComponent styling="p4" rootClassName={classNames(styles.viewOnlyText)}>
                  {t("View only")}
                </TypographyComponent>
              </div>
            )}
          </div>
          <div
            className={classNames(styles.title, !templateName && styles.noTemplate, isViewOnly && styles.viewOnlyTitle)}
          >
            <TypographyComponent styling="p1" boldness="medium" rootClassName={styles.titleWrap}>
              {initName}
            </TypographyComponent>
          </div>
          <EvalCardFooter products={initData.products} status={initData.status} lastActivity={lastActivity} />
        </div>
      </button>
      <TooltipV2
        data-testid="menu-icon"
        enterNextDelay={300}
        placement="top"
        PopperProps={{
          modifiers: [
            { name: "offset", options: { offset: [0, 0] } },
          ]
        }}
        title={kebabMenuTooltipText}
      >
        <div className={classNames(styles.kebabMenuOptions)}>
          <KebabMenuComponent
            onClickDelete={() => {
              setDeleteDialog(true);
            }}
            onClickArchive={async () => {
              await handleInitiativeArchive();
            }}
            isDisabled={!isOwner || isTrialUser}
          />
        </div>
      </TooltipV2>
      <DeleteDialog
        displayName={initName}
        handleClose={() => setDeleteDialog(false)}
        handleConfirm={handleConfirmDelete}
        isLoading={isLoading}
        isOpen={deleteDialog}
      />
    </div>
  );
};

export default EvalItemComponent;
